import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Download, Calendar as CalendarIcon, Filter, TrendingUp, DollarSign, Clock, Users, AlertTriangle, Building2, ChevronDown, ChevronRight, ChevronLeft, Sparkles, LayoutList, BarChart2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { format } from 'date-fns';

const decimalToHHMM = (decimal) => {
  if (!decimal || decimal <= 0) return '0:00';
  const totalMinutes = Math.round(decimal * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const ReportsPage = () => {
  const { api, canViewReports } = useAuth();
  const [loading, setLoading] = useState(true);
  const [utilization, setUtilization] = useState(null);
  const [departmentStats, setDepartmentStats] = useState(null);
  const [blockersReport, setBlockersReport] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(1)),
    to: new Date(),
  });
  const [pendingRange, setPendingRange] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(dateRange.from);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const [reportTab, setReportTab] = useState('utilization');
  const [allProjects, setAllProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [monthTimeEntries, setMonthTimeEntries] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [resourceMember, setResourceMember] = useState('all');
  const [resourceDepartment, setResourceDepartment] = useState('all');
  const [resourceStatus, setResourceStatus] = useState('active');
  const [projectAllocStatus, setProjectAllocStatus] = useState('active');
  const [expandedProject, setExpandedProject] = useState(null);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [monthPickerYear, setMonthPickerYear] = useState(() => parseInt(selectedMonth.split('-')[0]));
  const [viewMode, setViewMode] = useState('charts');
  const [reportEntries, setReportEntries] = useState([]);
  const [reportEntriesLoading, setReportEntriesLoading] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const datePresets = [
    { label: 'Today', fn: () => { const d = new Date(); return { from: d, to: d }; } },
    { label: 'This Week', fn: () => {
      const today = new Date(); const day = today.getDay();
      const mon = new Date(today); mon.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
      return { from: mon, to: new Date() };
    }},
    { label: 'Last Week', fn: () => {
      const today = new Date(); const day = today.getDay();
      const mon = new Date(today); mon.setDate(today.getDate() - (day === 0 ? 6 : day - 1) - 7);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return { from: mon, to: sun };
    }},
    { label: 'This Month', fn: () => ({ from: new Date(new Date().getFullYear(), new Date().getMonth(), 1), to: new Date() }) },
    { label: 'Last Month', fn: () => {
      const now = new Date(); const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      return { from: new Date(y, m, 1), to: new Date(y, m + 1, 0) };
    }},
    { label: 'Last 3 Months', fn: () => { const d = new Date(); const f = new Date(d); f.setMonth(d.getMonth() - 3); return { from: f, to: d }; } },
    { label: 'Last 6 Months', fn: () => { const d = new Date(); const f = new Date(d); f.setMonth(d.getMonth() - 6); return { from: f, to: d }; } },
    { label: 'This Year', fn: () => ({ from: new Date(new Date().getFullYear(), 0, 1), to: new Date() }) },
  ];

  const applyPreset = (preset) => {
    const range = preset.fn();
    setDateRange(range);
    setPendingRange(null);
    setCalendarMonth(range.from);
    setPickerOpen(false);
  };

  const applyPendingRange = () => {
    if (pendingRange?.from && pendingRange?.to) {
      setDateRange({ from: pendingRange.from, to: pendingRange.to });
    } else if (pendingRange?.from) {
      setDateRange({ from: pendingRange.from, to: pendingRange.from });
    }
    setPendingRange(null);
    setPickerOpen(false);
  };

  const activeRange = pendingRange || dateRange;
  const isPresetActive = (preset) => {
    const r = preset.fn();
    return r.from.toDateString() === dateRange.from.toDateString() && r.to.toDateString() === dateRange.to.toDateString();
  };

  const CHART_COLORS = ['hsl(221, 83%, 53%)', 'hsl(142, 71%, 45%)', 'hsl(262, 83%, 58%)', 'hsl(38, 92%, 50%)', 'hsl(0, 84%, 60%)'];

  useEffect(() => {
    fetchReportData();
    if (viewMode === 'table') fetchReportEntries();
  }, [dateRange, selectedDepartment, selectedMember]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const res = await api.get('/dashboard/capacity-planning');
        setMembers((res.data.team_capacity || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch { /* not all roles have access to capacity-planning */ }
    };
    fetchMembers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [projRes, usersRes] = await Promise.all([
          api.get('/projects'),
          api.get('/users?is_active=true'),
        ]);
        setAllProjects(projRes.data);
        setAllUsers([...usersRes.data].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)));
      } catch { /* not all roles have access */ }
    };
    fetchAllData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchMonthEntries = async () => {
      const [year, month] = selectedMonth.split('-').map(Number);
      const start = `${selectedMonth}-01`;
      const end = `${selectedMonth}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
      try {
        const res = await api.get(`/time-entries?date_from=${start}&date_to=${end}`);
        setMonthTimeEntries(res.data || []);
      } catch {
        setMonthTimeEntries([]);
      }
    };
    fetchMonthEntries();
  }, [selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const toLocalDateStr = (d) => {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dy = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${dy}`;
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const startDate = toLocalDateStr(dateRange.from);
      const endDate = toLocalDateStr(dateRange.to);
      let utilizationUrl = `/dashboard/utilization?start_date=${startDate}&end_date=${endDate}`;
      if (selectedMember !== 'all') utilizationUrl += `&user_id=${selectedMember}`;
      else if (selectedDepartment !== 'all') utilizationUrl += `&department=${selectedDepartment}`;

      const [utilRes, deptRes, blockersRes] = await Promise.all([
        api.get(utilizationUrl),
        api.get('/dashboard/department-stats'),
        api.get('/time-entries/blockers-report'),
      ]);
      setUtilization(utilRes.data);
      setDepartmentStats(deptRes.data);
      setBlockersReport(blockersRes.data);
    } catch (err) {
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const downloadBlob = async (url, filename, mimeType) => {
    const mimes = {
      csv: 'text/csv',
      excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      pdf: 'application/pdf',
    };
    const res = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: mimes[mimeType] || mimeType });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  };

  const fmtExt = (fmt) => ({ csv: 'csv', excel: 'xlsx', pdf: 'pdf' }[fmt] || fmt);

  const exportResourceAllocation = async (fmt) => {
    try {
      const memberParam = resourceMember !== 'all' ? `&user_id=${resourceMember}` : '';
      const deptParam = resourceDepartment !== 'all' ? `&department=${resourceDepartment}` : '';
      const statusParam = resourceStatus !== 'all' ? `&status=${resourceStatus}` : '';
      await downloadBlob(
        `/export/resource-allocation?month=${selectedMonth}&format=${fmt}${memberParam}${deptParam}${statusParam}`,
        `resource_allocation_${selectedMonth}.${fmtExt(fmt)}`,
        fmt
      );
      toast.success(`Resource Allocation ${fmt.toUpperCase()} exported`);
    } catch {
      toast.error('Failed to export');
    }
  };

  const exportProjectAllocation = async (fmt) => {
    try {
      const statusParam = projectAllocStatus !== 'all' ? `&status=${projectAllocStatus}` : '';
      await downloadBlob(
        `/export/project-allocation?format=${fmt}${statusParam}`,
        `project_allocation.${fmtExt(fmt)}`,
        fmt
      );
      toast.success(`Project Allocation ${fmt.toUpperCase()} exported`);
    } catch {
      toast.error('Failed to export');
    }
  };

  const fetchReportEntries = async () => {
    setReportEntriesLoading(true);
    try {
      const startDate = toLocalDateStr(dateRange.from);
      const endDate = toLocalDateStr(dateRange.to);
      const [entriesRes, verticalsRes, workTypesRes] = await Promise.all([
        api.get(`/time-entries?date_from=${startDate}&date_to=${endDate}`),
        api.get('/service-catalog/verticals'),
        api.get('/service-catalog/work-types'),
      ]);
      const verticalMap = {};
      for (const v of (verticalsRes.data || [])) verticalMap[v.id] = v.name;
      const workTypeMap = {};
      for (const wt of (workTypesRes.data || [])) workTypeMap[wt.id] = wt.name;
      const enriched = (entriesRes.data || []).map(e => ({
        ...e,
        vertical_name: e.vertical_id ? (verticalMap[e.vertical_id] || null) : null,
        work_type_name: e.work_type_id ? (workTypeMap[e.work_type_id] || null) : null,
      }));
      setReportEntries(enriched);
    } catch {
      toast.error('Failed to fetch report entries');
    } finally {
      setReportEntriesLoading(false);
    }
  };

  const exportReport = async (fmt) => {
    try {
      const startDate = toLocalDateStr(dateRange.from);
      const endDate = toLocalDateStr(dateRange.to);
      const deptParam = selectedDepartment !== 'all' ? `&department=${selectedDepartment}` : '';
      const memberParam = selectedMember !== 'all' ? `&user_id=${selectedMember}` : '';
      const url = `/export/report?start_date=${startDate}&end_date=${endDate}&format=${fmt}${deptParam}${memberParam}`;

      const mimeTypes = {
        csv: 'text/csv',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pdf: 'application/pdf',
      };
      const extensions = { csv: 'csv', excel: 'xlsx', pdf: 'pdf' };

      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: mimeTypes[fmt] });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `report_${startDate}_${endDate}.${extensions[fmt]}`;
      link.click();
      window.URL.revokeObjectURL(link.href);
      toast.success(`${fmt.toUpperCase()} report exported`);
    } catch (err) {
      toast.error('Failed to export report');
    }
  };

  const dailyData = utilization?.daily_hours
    ? Object.entries(utilization.daily_hours).map(([date, data]) => ({
        date: format(new Date(date), 'MMM d'),
        total: parseFloat((data.total || 0).toFixed(2)),
        billable: parseFloat((data.billable || 0).toFixed(2)),
      }))
    : [];


  const filteredMembers = selectedDepartment === 'all'
    ? members
    : members.filter(m => m.department === selectedDepartment);

  const deptData = (departmentStats?.departments || [])
    .filter(d => selectedDepartment === 'all' || d.department === selectedDepartment)
    .map((d, i) => ({
      name: d.department.replace(/_/g, ' '),
      total: Math.round(d.total),
      billable: Math.round(d.billable),
      fill: CHART_COLORS[i % CHART_COLORS.length],
    }));

  const projectData = utilization?.project_breakdown?.slice(0, 8).map((p, i) => ({
    name: p.project_name.length > 20 ? p.project_name.substring(0, 20) + '...' : p.project_name,
    hours: parseFloat((p.hours || 0).toFixed(2)),
    fill: CHART_COLORS[i % CHART_COLORS.length],
  })) || [];

  const totalHours = utilization?.total_hours || 0;
  const billableHours = utilization?.billable_hours || 0;
  const billableRatio = totalHours > 0 ? Math.round((billableHours / totalHours) * 100) : 0;
  const avgDailyHours = dailyData.length > 0 ? (totalHours / dailyData.length).toFixed(1) : 0;

  const monthOptions = useMemo(() => {
    const months = new Set();
    const now = new Date();
    for (let i = -12; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return [...months].sort();
  }, []);

  const allDepartments = useMemo(() => {
    const depts = new Set(allUsers.map(u => u.department).filter(Boolean));
    return [...depts].sort();
  }, [allUsers]);

  const resourceData = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);
    const projectsInMonth = allProjects.filter(p => {
      if (resourceStatus !== 'all' && p.status !== resourceStatus) return false;
      const start = p.start_date ? new Date(p.start_date + 'T00:00:00') : null;
      const end = p.end_date ? new Date(p.end_date + 'T00:00:00') : null;
      if (!start && !end) return true;
      if (start && start > monthEnd) return false;
      if (end && end < monthStart) return false;
      return true;
    });
    const grouped = {};
    projectsInMonth.forEach(p => {
      const allocations = resourceMember === 'all'
        ? (p.allocations || [])
        : (p.allocations || []).filter(a => a.user_id === resourceMember);
      allocations.forEach(alloc => {
        const u = allUsers.find(x => x.id === alloc.user_id);
        if (!u) return;
        if (resourceDepartment !== 'all' && u.department !== resourceDepartment) return;
        if (!grouped[alloc.user_id]) {
          grouped[alloc.user_id] = { user_id: alloc.user_id, name: `${u.first_name} ${u.last_name}`, department: u.department || '—', projects: [], totalHours: 0, totalUtilized: 0 };
        }
        const utilized = monthTimeEntries
          .filter(e => e.user_id === alloc.user_id && e.project_id === p.id)
          .reduce((s, e) => s + (e.hours || 0), 0);
        const effectiveAllocHours = (p.project_type === 'monthly_retainer' && !(alloc.allocated_hours > 0) && (p.monthly_budget_hours || 0) > 0)
          ? p.monthly_budget_hours
          : (alloc.allocated_hours || 0);
        grouped[alloc.user_id].projects.push({
          project_id: p.id, project_name: p.name, client_name: p.client_name,
          department: p.department, project_type: p.project_type,
          start_date: p.start_date, end_date: p.end_date,
          allocated_hours: effectiveAllocHours,
          utilized_hours: utilized,
          is_retainer_fallback: p.project_type === 'monthly_retainer' && !(alloc.allocated_hours > 0) && (p.monthly_budget_hours || 0) > 0,
        });
        grouped[alloc.user_id].totalHours += effectiveAllocHours;
        grouped[alloc.user_id].totalUtilized += utilized;
      });
    });
    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [allProjects, allUsers, selectedMonth, resourceMember, resourceDepartment, resourceStatus, monthTimeEntries]);

  const projectAllocationData = useMemo(() => allProjects
    .filter(p => projectAllocStatus === 'all' || p.status === projectAllocStatus)
    .map(p => ({
      ...p,
      team: (p.allocations || []).map(a => {
        const u = allUsers.find(x => x.id === a.user_id);
        return { ...a, name: u ? `${u.first_name} ${u.last_name}` : 'Unknown', department: u?.department };
      }),
    })), [allProjects, allUsers, projectAllocStatus]);

  if (!canViewReports) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Access Restricted</h2>
          <p className="text-muted-foreground mt-2">You don't have permission to view reports</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-600/10 via-indigo-500/5 to-violet-400/10 p-6">
        <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary/5 blur-2xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-background/70 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm">
            <TrendingUp className="w-7 h-7 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Analytics Dashboard</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">Reports & Analytics</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-primary/60" />
              Comprehensive time tracking analytics
            </p>
          </div>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border -mb-2" data-tour="report-type-tabs">
        {[
          { key: 'utilization', label: 'Time & Utilization' },
          { key: 'resource-allocation', label: 'Resource Allocation' },
          { key: 'project-allocation', label: 'Project Allocation' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setReportTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              reportTab === tab.key
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Time & Utilization ── */}
      {reportTab === 'utilization' && (<>
        {/* Unified Toolbar */}
        <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/40 border border-border rounded-lg">
          {/* Date Range Picker */}
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 gap-2 text-sm font-medium bg-background justify-start" data-tour="date-range-picker">
                <CalendarIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="tabular-nums">{format(dateRange.from, 'MMM d, yyyy')} — {format(dateRange.to, 'MMM d, yyyy')}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0 flex flex-col sm:flex-row shadow-xl" side="bottom">
              <div className="flex flex-col gap-0.5 p-3 border-r border-border w-36 shrink-0">
                {datePresets.map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => applyPreset(preset)}
                    className={`text-left px-3 py-1.5 rounded text-sm transition-colors ${
                      isPresetActive(preset)
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="p-3">
                <Calendar
                  mode="range"
                  selected={activeRange}
                  onSelect={setPendingRange}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  numberOfMonths={isMobile ? 1 : 2}
                />
                <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-border">
                  <Button variant="ghost" size="sm" onClick={() => { setPendingRange(null); setPickerOpen(false); }}>Cancel</Button>
                  <Button size="sm" onClick={applyPendingRange} disabled={!pendingRange?.from}>Apply</Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <div className="w-px h-6 bg-border hidden sm:block" />

          <div className="flex items-center gap-2" data-tour="report-filters">
          {/* Department Filter */}
          <Select value={selectedDepartment} onValueChange={(v) => { setSelectedDepartment(v); setSelectedMember('all'); }}>
            <SelectTrigger className="h-9 w-[175px] bg-background text-sm">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {allDepartments.map(d => (
                <SelectItem key={d} value={d} className="capitalize">{d.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Member Filter */}
          {members.length > 0 && (
            <Select value={selectedMember} onValueChange={setSelectedMember}>
              <SelectTrigger className="h-9 w-[155px] bg-background text-sm">
                <SelectValue placeholder="All Members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                {filteredMembers.map(m => (
                  <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          </div>

          {/* Clear active filters */}
          {(selectedDepartment !== 'all' || selectedMember !== 'all') && (
            <Button variant="ghost" size="sm" className="h-9 px-3 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => { setSelectedDepartment('all'); setSelectedMember('all'); }}>
              Clear filters
            </Button>
          )}

          {/* View toggle — pushed to the right on row 1 */}
          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center border border-border rounded-md overflow-hidden bg-background">
              <button
                onClick={() => setViewMode('charts')}
                className={`flex items-center gap-1.5 px-3 h-9 text-xs font-medium transition-colors ${viewMode === 'charts' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              >
                <BarChart2 className="w-3.5 h-3.5" /> Charts
              </button>
              <button
                onClick={() => { setViewMode('table'); fetchReportEntries(); }}
                className={`flex items-center gap-1.5 px-3 h-9 text-xs font-medium transition-colors border-l border-border ${viewMode === 'table' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              >
                <LayoutList className="w-3.5 h-3.5" /> Table
              </button>
            </div>
            {/* Export buttons — desktop only in this row */}
            <div className="hidden sm:flex items-center gap-1.5" data-tour="export-buttons">
              <div className="w-px h-6 bg-border" />
              <span className="text-xs text-muted-foreground">Export:</span>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium" onClick={() => exportReport('csv')}>
                <Download className="w-3.5 h-3.5" /> CSV
              </Button>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium" onClick={() => exportReport('excel')}>
                <Download className="w-3.5 h-3.5" /> Excel
              </Button>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium" onClick={() => exportReport('pdf')}>
                <Download className="w-3.5 h-3.5" /> PDF
              </Button>
            </div>
          </div>

          {/* Export buttons — mobile row 2 */}
          <div className="flex sm:hidden items-center gap-1.5 w-full pt-1 border-t border-border/50">
            <span className="text-xs text-muted-foreground mr-1">Export:</span>
            <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium flex-1" onClick={() => exportReport('csv')}>
              <Download className="w-3.5 h-3.5" /> CSV
            </Button>
            <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium flex-1" onClick={() => exportReport('excel')}>
              <Download className="w-3.5 h-3.5" /> Excel
            </Button>
            <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium flex-1" onClick={() => exportReport('pdf')}>
              <Download className="w-3.5 h-3.5" /> PDF
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
        <>
          {viewMode === 'charts' && <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-tour="kpi-cards">
            <Card className="card-hover" data-testid="total-hours-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-sm flex items-center justify-center">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                </div>
                <div className="text-3xl font-bold">{Math.round(totalHours)}h</div>
                <div className="text-sm text-muted-foreground">Total Hours</div>
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="billable-hours-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                </div>
                <div className="text-3xl font-bold">{Math.round(billableHours)}h</div>
                <div className="text-sm text-muted-foreground">Billable Hours</div>
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="utilization-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
                <div className="text-3xl font-bold">{billableRatio}%</div>
                <div className="text-sm text-muted-foreground">Billable Ratio</div>
                <Progress value={billableRatio} className="mt-3 h-1" />
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="avg-daily-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-purple-500/10 rounded-sm flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-500" />
                  </div>
                </div>
                <div className="text-3xl font-bold">{avgDailyHours}h</div>
                <div className="text-sm text-muted-foreground">Avg Daily Hours</div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card data-testid="daily-trend-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Daily Hours Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyData}>
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '4px'
                        }}
                        formatter={(v) => `${parseFloat(v).toFixed(2)}h`}
                      />
                      <Line type="monotone" dataKey="total" stroke="hsl(var(--accent))" strokeWidth={2} dot={false} name="Total" />
                      <Line type="monotone" dataKey="billable" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} name="Billable" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="department-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Hours by Department</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={deptData} layout="vertical">
                      <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} width={100} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '4px'
                        }}
                        formatter={(v) => `${parseFloat(v).toFixed(2)}h`}
                      />
                      <Bar dataKey="total" name="Total Hours" radius={[0, 4, 4, 0]}>
                        {deptData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Blockers by Project */}
          {blockersReport.length > 0 && (
            <Card data-tour="blockers-section">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                  <CardTitle className="text-lg font-semibold">Blockers by Project</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Reported blockers and challenges aggregated per project.</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {blockersReport.map((proj) => (
                    <div key={proj.project_id} className="border border-border rounded-md p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{proj.project_name}</span>
                        <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">
                          {proj.blockers.length} {proj.blockers.length === 1 ? 'blocker' : 'blockers'}
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {proj.blockers.filter(b => b.text).map((b, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <span className="text-destructive mt-0.5 flex-shrink-0">⚠</span>
                            <div>
                              <span className="text-foreground">{b.text}</span>
                              <span className="text-xs text-muted-foreground ml-2">— {b.date}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card data-testid="project-distribution-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Project Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {projectData.length > 0 ? (
                  <>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={projectData}
                            dataKey="hours"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={90}
                            paddingAngle={2}
                          >
                            {projectData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '4px'
                            }}
                            formatter={(value) => [`${parseFloat(value).toFixed(2)}h`, 'Hours']}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                      {projectData.map((entry, index) => (
                        <div key={index} className="flex items-center gap-1.5 min-w-0">
                          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: entry.fill }} />
                          <span className="text-xs text-muted-foreground truncate">{entry.name}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-56 flex items-center justify-center text-muted-foreground text-sm">
                    No project data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="billable-breakdown">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Billable vs Non-Billable</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6 pt-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Billable Hours</span>
                      <span className="font-mono">{Math.round(billableHours)}h ({billableRatio}%)</span>
                    </div>
                    <Progress value={billableRatio} className="h-3" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Non-Billable Hours</span>
                      <span className="font-mono">{Math.round(totalHours - billableHours)}h ({100 - billableRatio}%)</span>
                    </div>
                    <Progress value={100 - billableRatio} className="h-3 [&>div]:bg-muted-foreground" />
                  </div>
                  
                  <div className="pt-4 border-t border-border">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 border border-border rounded-sm">
                        <div className="text-2xl font-bold text-accent">{deptData.length}</div>
                        <div className="text-sm text-muted-foreground">Departments Active</div>
                      </div>
                      <div className="p-4 border border-border rounded-sm">
                        <div className="text-2xl font-bold text-accent">{projectData.length}</div>
                        <div className="text-sm text-muted-foreground">Projects Tracked</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          </>}

          {viewMode === 'table' && (() => {
            const filtered = reportEntries.filter(e => {
              if (selectedMember !== 'all' && e.user_id !== selectedMember) return false;
              if (selectedDepartment !== 'all') {
                const u = allUsers.find(u => u.id === e.user_id);
                if (u?.department !== selectedDepartment) return false;
              }
              return true;
            }).sort((a, b) => b.date.localeCompare(a.date));
            const totalHrs = filtered.reduce((s, e) => s + (e.hours || 0), 0);
            const billableHrs = filtered.filter(e => e.is_billable !== false).reduce((s, e) => s + (e.hours || 0), 0);
            const totalAmt = filtered.reduce((s, e) => {
              const p = allProjects.find(p => p.id === e.project_id);
              return s + (e.is_billable !== false ? (e.hours * (p?.billing_rate || 0)) : 0);
            }, 0);
            return (
              <Card className="overflow-hidden">
                {/* Summary stat strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 border-b border-border divide-x divide-border">
                  {[
                    { label: 'Total Entries', value: filtered.length, fmt: v => v, color: 'text-foreground' },
                    { label: 'Total Hours', value: totalHrs, fmt: v => `${decimalToHHMM(v)}h`, color: 'text-accent' },
                    { label: 'Billable Hours', value: billableHrs, fmt: v => `${decimalToHHMM(v)}h`, color: 'text-green-600 dark:text-green-400' },
                    { label: 'Total Amount', value: totalAmt, fmt: v => v > 0 ? `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—', color: totalAmt > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground' },
                  ].map(s => (
                    <div key={s.label} className="px-5 py-4 bg-muted/20">
                      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">{s.label}</div>
                      <div className={`text-2xl font-bold font-mono ${s.color}`}>{s.fmt(s.value)}</div>
                    </div>
                  ))}
                </div>
                <CardContent className="p-0">
                  {reportEntriesLoading ? (
                    <div className="flex items-center justify-center h-40">
                      <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                      <LayoutList className="w-10 h-10 text-muted-foreground/40" />
                      <p className="text-muted-foreground text-sm">No entries found for the selected period.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[1600px] text-sm">
                        <thead>
                          <tr className="border-b border-border bg-muted/50">
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[100px]">Date</th>
                            <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[130px]">Employee</th>
                            <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[110px]">Department</th>
                            <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[180px]">Project</th>
                            <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[110px]">Client</th>
                            <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[140px]">Work Type</th>
                            <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[100px]">Vertical</th>
                            <th className="text-right px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[110px]">Hours Utilized</th>
                            <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[80px]">Billable</th>
                            <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground w-[200px]">Description</th>
                            <th className="text-right px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[90px]">Alloc. Hrs</th>
                            <th className="text-right px-3 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[90px]">Rate</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap w-[100px]">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border/50">
                          {filtered.map((entry, i) => {
                            const u = allUsers.find(u => u.id === entry.user_id);
                            const p = allProjects.find(p => p.id === entry.project_id);
                            const alloc = (p?.allocations || []).find(a => a.user_id === entry.user_id);
                            const rate = entry.is_billable !== false ? (p?.billing_rate || 0) : 0;
                            const amt = rate ? (entry.hours * rate) : null;
                            const initials = u ? `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase() : '?';
                            const isBillable = entry.is_billable !== false;
                            return (
                              <tr key={entry.id || i} className="hover:bg-muted/40 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-muted-foreground">{entry.date}</td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary flex-shrink-0 select-none">{initials}</div>
                                    <span className="font-medium text-sm">{u ? `${u.first_name} ${u.last_name}` : '—'}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap capitalize text-xs text-muted-foreground">{u?.department?.replace(/_/g, ' ') || '—'}</td>
                                <td className="px-3 py-3 whitespace-nowrap font-medium">{p?.name || '—'}</td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs text-muted-foreground">{p?.client_name || 'Internal'}</td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs text-muted-foreground">{entry.work_type_name || <span className="opacity-30">—</span>}</td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs text-muted-foreground">{entry.vertical_name || <span className="opacity-30">—</span>}</td>
                                <td className="px-3 py-3 text-right whitespace-nowrap">
                                  <span className="font-mono font-bold text-accent">{decimalToHHMM(entry.hours)}h</span>
                                </td>
                                <td className="px-3 py-3 text-center">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${isBillable ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                                    {isBillable ? 'Billable' : 'Non-bill'}
                                  </span>
                                </td>
                                <td className="px-3 py-3 max-w-[200px]">
                                  <span className="text-xs text-muted-foreground break-words whitespace-normal block">{entry.description || <span className="opacity-30">—</span>}</span>
                                </td>
                                <td className="px-3 py-3 text-right whitespace-nowrap font-mono text-xs">{alloc ? `${alloc.allocated_hours}h` : <span className="opacity-30">—</span>}</td>
                                <td className="px-3 py-3 text-right whitespace-nowrap font-mono text-xs text-muted-foreground">{rate ? `₹${rate}/hr` : <span className="opacity-30">—</span>}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap font-mono font-semibold">{amt != null ? `₹${amt.toFixed(2)}` : <span className="opacity-30 font-normal">—</span>}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-border bg-muted/50 font-semibold">
                            <td className="px-4 py-3 text-xs text-muted-foreground" colSpan={7}>{filtered.length} entries</td>
                            <td className="px-3 py-3 text-right font-mono font-bold text-accent">{decimalToHHMM(totalHrs)}h</td>
                            <td className="px-3 py-3 text-center text-xs text-green-600 dark:text-green-400 font-mono">{decimalToHHMM(billableHrs)}h</td>
                            <td colSpan={3} />
                            <td className="px-4 py-3 text-right font-mono font-bold">{totalAmt > 0 ? `₹${totalAmt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </>
        )}
      </>)}

      {/* ── Resource Allocation ── */}
      {reportTab === 'resource-allocation' && (
        <div className="space-y-4">
          {/* Unified Toolbar */}
          <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/40 border border-border rounded-lg">
            {/* Month Picker */}
            <Popover open={monthPickerOpen} onOpenChange={setMonthPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 gap-2 text-sm font-medium bg-background justify-start min-w-[185px]">
                  <CalendarIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                  {new Date(parseInt(selectedMonth.split('-')[0]), parseInt(selectedMonth.split('-')[1]) - 1, 1)
                    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-60 p-3 shadow-xl" side="bottom">
                {/* Year navigation */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setMonthPickerYear(y => y - 1)}
                    className="p-1.5 rounded hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-semibold">{monthPickerYear}</span>
                  <button
                    onClick={() => setMonthPickerYear(y => y + 1)}
                    className="p-1.5 rounded hover:bg-muted transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                {/* Month grid */}
                <div className="grid grid-cols-3 gap-1">
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((name, i) => {
                    const key = `${monthPickerYear}-${String(i + 1).padStart(2, '0')}`;
                    const isSelected = key === selectedMonth;
                    const now = new Date();
                    const isNow = key === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    return (
                      <button
                        key={name}
                        onClick={() => { setSelectedMonth(key); setMonthPickerOpen(false); }}
                        className={`py-2 rounded text-sm transition-colors ${
                          isSelected
                            ? 'bg-accent text-accent-foreground font-semibold'
                            : isNow
                            ? 'bg-muted font-medium text-foreground'
                            : 'hover:bg-muted text-foreground'
                        }`}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>

            <div className="w-px h-6 bg-border hidden sm:block" />

            {/* Status Filter */}
            <Select value={resourceStatus} onValueChange={setResourceStatus}>
              <SelectTrigger className="h-9 w-[145px] bg-background text-sm">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* Department Filter */}
            <Select value={resourceDepartment} onValueChange={(v) => { setResourceDepartment(v); setResourceMember('all'); }}>
              <SelectTrigger className="h-9 w-[175px] bg-background text-sm">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {allDepartments.map(d => (
                  <SelectItem key={d} value={d} className="capitalize">{d.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Member Filter */}
            <Select value={resourceMember} onValueChange={setResourceMember}>
              <SelectTrigger className="h-9 w-[155px] bg-background text-sm">
                <SelectValue placeholder="All Members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                {(resourceDepartment === 'all' ? allUsers : allUsers.filter(u => u.department === resourceDepartment))
                  .map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Clear filters */}
            {(resourceStatus !== 'all' || resourceDepartment !== 'all' || resourceMember !== 'all') && (
              <Button variant="ghost" size="sm" className="h-9 px-3 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => { setResourceStatus('all'); setResourceDepartment('all'); setResourceMember('all'); }}>
                Clear filters
              </Button>
            )}

            {/* Result count + Export — pushed to the right */}
            <div className="flex items-center gap-3 ml-auto">
              <span className="text-xs text-muted-foreground hidden sm:block">
                {resourceData.length} {resourceData.length === 1 ? 'member' : 'members'} with allocations
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground hidden sm:block mr-1">Export:</span>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium" onClick={() => exportResourceAllocation('csv')}>
                  <Download className="w-3.5 h-3.5" /> CSV
                </Button>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium" onClick={() => exportResourceAllocation('excel')}>
                  <Download className="w-3.5 h-3.5" /> Excel
                </Button>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium" onClick={() => exportResourceAllocation('pdf')}>
                  <Download className="w-3.5 h-3.5" /> PDF
                </Button>
              </div>
            </div>
          </div>

          {resourceData.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
                <Users className="w-10 h-10 text-muted-foreground/40" />
                <p className="text-muted-foreground">No allocations found for the selected month and member.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {resourceData.map(member => (
                <Card key={member.user_id}>
                  <CardHeader className="pb-3 pt-4 px-5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary flex-shrink-0 select-none">
                          {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <div className="font-semibold text-sm">{member.name}</div>
                          <div className="text-xs text-muted-foreground capitalize">{member.department?.replace(/_/g, ' ')}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Utilized</div>
                          <div className="font-bold text-foreground">{decimalToHHMM(member.totalUtilized)}h</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Total Allocated</div>
                          <div className="font-bold text-accent">{member.totalHours}h</div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {member.projects.length} project{member.projects.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-t border-border bg-muted/40">
                            <th className="text-left px-5 py-2 text-xs font-medium text-muted-foreground">Project</th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Client</th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Department</th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Type</th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Active in Month</th>
                            <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Alloc. Hours</th>
                            <th className="text-right px-5 py-2 text-xs font-medium text-muted-foreground">Utilized</th>
                          </tr>
                        </thead>
                        <tbody>
                          {member.projects.map((proj, i) => {
                            const [selY, selM] = selectedMonth.split('-').map(Number);
                            const mStart = `${selectedMonth}-01`;
                            const mEnd = `${selectedMonth}-${String(new Date(selY, selM, 0).getDate()).padStart(2, '0')}`;
                            const dispStart = proj.start_date && proj.start_date > mStart ? proj.start_date : mStart;
                            const dispEnd = proj.end_date && proj.end_date < mEnd ? proj.end_date : mEnd;
                            const fmt = d => { const [y,m,day] = d.split('-'); return `${parseInt(m)}/${parseInt(day)}/${y}`; };
                            return (
                            <tr key={proj.project_id} className={`border-t border-border/50 hover:bg-muted/30 ${i % 2 !== 0 ? 'bg-muted/10' : ''}`}>
                              <td className="px-5 py-2.5 font-medium">{proj.project_name}</td>
                              <td className="px-3 py-2.5 text-muted-foreground">{proj.client_name || 'Internal'}</td>
                              <td className="px-3 py-2.5 text-muted-foreground capitalize">{proj.department?.replace(/_/g, ' ') || '—'}</td>
                              <td className="px-3 py-2.5">
                                <Badge variant="secondary" className="text-xs capitalize">
                                  {(proj.project_type || 'fixed').replace(/_/g, ' ')}
                                </Badge>
                              </td>
                              <td className="px-3 py-2.5 text-muted-foreground text-xs whitespace-nowrap">
                                {fmt(dispStart)} → {fmt(dispEnd)}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono font-semibold">
                                {proj.allocated_hours}h
                                {proj.is_retainer_fallback && (
                                  <span className="ml-1 text-[10px] text-muted-foreground font-normal" title="Monthly cap used — no individual allocation set">(cap)</span>
                                )}
                              </td>
                              <td className="px-5 py-2.5 text-right">
                                <div className="flex flex-col items-end gap-1">
                                  <span className={`font-mono font-semibold text-sm ${proj.utilized_hours > proj.allocated_hours ? 'text-red-500' : proj.utilized_hours > 0 ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                                    {decimalToHHMM(proj.utilized_hours)}h
                                  </span>
                                  {proj.allocated_hours > 0 && (
                                    <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all ${proj.utilized_hours > proj.allocated_hours ? 'bg-red-500' : 'bg-green-500'}`}
                                        style={{ width: `${Math.min(100, (proj.utilized_hours / proj.allocated_hours) * 100)}%` }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Project Allocation ── */}
      {reportTab === 'project-allocation' && (
        <div className="space-y-3">
          {/* Unified Toolbar */}
          <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/40 border border-border rounded-lg">
            {/* Month Picker */}
            <Popover open={monthPickerOpen} onOpenChange={setMonthPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 gap-2 text-sm font-medium bg-background justify-start min-w-[185px]">
                  <CalendarIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                  {new Date(parseInt(selectedMonth.split('-')[0]), parseInt(selectedMonth.split('-')[1]) - 1, 1)
                    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-60 p-3 shadow-xl" side="bottom">
                <div className="flex items-center justify-between mb-3">
                  <button onClick={() => setMonthPickerYear(y => y - 1)} className="p-1.5 rounded hover:bg-muted transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-semibold">{monthPickerYear}</span>
                  <button onClick={() => setMonthPickerYear(y => y + 1)} className="p-1.5 rounded hover:bg-muted transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((name, i) => {
                    const key = `${monthPickerYear}-${String(i + 1).padStart(2, '00')}`;
                    const isSelected = key === selectedMonth;
                    const now = new Date();
                    const isNow = key === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    return (
                      <button key={name} onClick={() => { setSelectedMonth(key); setMonthPickerOpen(false); }}
                        className={`py-2 rounded text-sm transition-colors ${isSelected ? 'bg-accent text-accent-foreground font-semibold' : isNow ? 'bg-muted font-medium text-foreground' : 'hover:bg-muted text-foreground'}`}>
                        {name}
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>

            <div className="w-px h-6 bg-border hidden sm:block" />

            {/* Status Filter */}
            <Select value={projectAllocStatus} onValueChange={setProjectAllocStatus}>
              <SelectTrigger className="h-9 w-[145px] bg-background text-sm">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <span className="text-xs text-muted-foreground">
              {projectAllocationData.length} {projectAllocationData.length === 1 ? 'project' : 'projects'}
            </span>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-xs text-muted-foreground hidden sm:block mr-1">Export:</span>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium" onClick={() => exportProjectAllocation('csv')}>
                <Download className="w-3.5 h-3.5" /> CSV
              </Button>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium" onClick={() => exportProjectAllocation('excel')}>
                <Download className="w-3.5 h-3.5" /> Excel
              </Button>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 bg-background text-xs font-medium" onClick={() => exportProjectAllocation('pdf')}>
                <Download className="w-3.5 h-3.5" /> PDF
              </Button>
            </div>
          </div>
          {projectAllocationData.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
                <Building2 className="w-10 h-10 text-muted-foreground/40" />
                <p className="text-muted-foreground">No projects found.</p>
              </CardContent>
            </Card>
          ) : (
            projectAllocationData.map(proj => {
              const isExpanded = expandedProject === proj.id;
              const statusColors = {
                active: 'bg-green-500/10 text-green-600 dark:text-green-400',
                completed: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
                on_hold: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
                cancelled: 'bg-red-500/10 text-red-600 dark:text-red-400',
              };
              const priorityColors = {
                high: 'bg-red-500/10 text-red-600 dark:text-red-400',
                medium: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
                low: 'bg-green-500/10 text-green-600 dark:text-green-400',
              };
              return (
                <Card key={proj.id} className="overflow-hidden">
                  <div
                    className="flex items-start justify-between gap-4 px-5 py-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setExpandedProject(prev => prev === proj.id ? null : proj.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{proj.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusColors[proj.status] || 'bg-muted text-muted-foreground'}`}>
                          {(proj.status || 'active').replace(/_/g, ' ')}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${priorityColors[proj.priority] || 'bg-muted text-muted-foreground'}`}>
                          {proj.priority || 'medium'}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                        {proj.client_name && <span>Client: <span className="text-foreground">{proj.client_name}</span></span>}
                        {proj.department && <span>Dept: <span className="text-foreground capitalize">{proj.department.replace(/_/g, ' ')}</span></span>}
                        {proj.project_type && <span>Type: <span className="text-foreground capitalize">{proj.project_type.replace(/_/g, ' ')}</span></span>}
                        {proj.start_date && <span>Start: <span className="text-foreground">{proj.start_date}</span></span>}
                        {proj.end_date && <span>End: <span className="text-foreground">{proj.end_date}</span></span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="hidden sm:grid grid-cols-4 gap-4 text-center">
                        <div>
                          <div className="text-xs text-muted-foreground">Budget</div>
                          <div className="text-sm font-semibold">{proj.budgeted_hours || 0}h</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Monthly</div>
                          <div className="text-sm font-semibold">{proj.monthly_budget_hours ? `${proj.monthly_budget_hours}h` : '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Team</div>
                          <div className="text-sm font-semibold">{proj.team.length}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Utilized</div>
                          <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {decimalToHHMM(monthTimeEntries.filter(e => e.project_id === proj.id).reduce((s, e) => s + (e.hours || 0), 0))}h
                          </div>
                        </div>
                      </div>
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-border">
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-border">
                        {[
                          { label: 'Budgeted Hours', value: `${proj.budgeted_hours || 0}h` },
                          { label: 'Monthly Budget', value: proj.monthly_budget_hours ? `${proj.monthly_budget_hours}h` : '—' },
                          { label: 'Billing Rate', value: proj.billing_rate ? `₹${proj.billing_rate}/hr` : '—' },
                          { label: 'Cost Rate', value: proj.cost_rate ? `₹${proj.cost_rate}/hr` : '—' },
                          { label: 'Hours Logged', value: `${Math.round((proj.total_logged_hours || 0) * 10) / 10}h` },
                        ].map(item => (
                          <div key={item.label} className="bg-muted/30 px-4 py-3">
                            <div className="text-xs text-muted-foreground">{item.label}</div>
                            <div className="text-sm font-semibold mt-0.5">{item.value}</div>
                          </div>
                        ))}
                      </div>

                      {proj.team.length > 0 ? (
                        <div>
                          <div className="px-5 pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Team Allocations</div>
                          <div className="overflow-x-auto">
                          <table className="w-full min-w-[520px] text-sm">
                            <thead>
                              <tr className="border-t border-border bg-muted/20">
                                <th className="text-left px-5 py-2 text-xs font-medium text-muted-foreground">Member</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Department</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Allocated Hours</th>
                                <th className="text-right px-5 py-2 text-xs font-medium text-muted-foreground">
                                  Utilized <span className="text-muted-foreground/50 font-normal">({new Date(parseInt(selectedMonth.split('-')[0]), parseInt(selectedMonth.split('-')[1]) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {proj.team.map((m, i) => {
                                const utilized = monthTimeEntries
                                  .filter(e => e.user_id === m.user_id && e.project_id === proj.id)
                                  .reduce((s, e) => s + (e.hours || 0), 0);
                                const allocHours = (proj.project_type === 'monthly_retainer' && !(m.allocated_hours > 0) && (proj.monthly_budget_hours || 0) > 0)
                                  ? proj.monthly_budget_hours
                                  : (m.allocated_hours || 0);
                                const isRetainerFallback = proj.project_type === 'monthly_retainer' && !(m.allocated_hours > 0) && (proj.monthly_budget_hours || 0) > 0;
                                const isOver = utilized > allocHours && allocHours > 0;
                                return (
                                  <tr key={m.user_id || i} className="border-t border-border/50 hover:bg-muted/30">
                                    <td className="px-5 py-2.5 font-medium">{m.name}</td>
                                    <td className="px-3 py-2.5 text-muted-foreground capitalize">{m.department?.replace(/_/g, ' ') || '—'}</td>
                                    <td className="px-3 py-2.5 text-right font-mono font-semibold">
                                      {allocHours}h
                                      {isRetainerFallback && (
                                        <span className="ml-1 text-[10px] text-muted-foreground font-normal" title="Monthly cap used — no individual allocation set">(cap)</span>
                                      )}
                                    </td>
                                    <td className="px-5 py-2.5 text-right">
                                      <div className="flex flex-col items-end gap-1">
                                        <span className={`font-mono font-semibold text-sm ${isOver ? 'text-red-500' : utilized > 0 ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                                          {decimalToHHMM(utilized)}h
                                        </span>
                                        {allocHours > 0 && (
                                          <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                                            <div
                                              className={`h-full rounded-full transition-all ${isOver ? 'bg-red-500' : 'bg-green-500'}`}
                                              style={{ width: `${Math.min(100, (utilized / allocHours) * 100)}%` }}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr className="border-t border-border bg-muted/30 font-semibold">
                                <td className="px-5 py-2 text-sm" colSpan={2}>Total Allocated</td>
                                <td className="px-3 py-2 text-right font-mono text-accent">
                                  {Math.round(proj.team.reduce((s, m) => {
                                    const effH = (proj.project_type === 'monthly_retainer' && !(m.allocated_hours > 0) && (proj.monthly_budget_hours || 0) > 0)
                                      ? proj.monthly_budget_hours
                                      : (m.allocated_hours || 0);
                                    return s + effH;
                                  }, 0) * 10) / 10}h
                                </td>
                                <td className="px-5 py-2 text-right font-mono text-green-600 dark:text-green-400">
                                  {decimalToHHMM(monthTimeEntries.filter(e => e.project_id === proj.id).reduce((s, e) => s + (e.hours || 0), 0))}h
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          </div>
                        </div>
                      ) : (
                        <div className="px-5 py-4 text-sm text-muted-foreground">No team members allocated.</div>
                      )}

                      {proj.description && (
                        <div className="px-5 py-3 border-t border-border">
                          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Description</div>
                          <p className="text-sm text-muted-foreground">{proj.description}</p>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
